import { Col, Row, Table } from "antd"
import { ProfilePageData } from "libs/profile-repo"
import { dateFormat, getEnumLabel } from "libs/utils"
import React from "react"
import ProfileDetailItem from "./ProfileDetailItem"
import ProfileTitle from "./ProfileTitle"

export default function ProfileBusinessDetail({
  businessDetail,
  enumState,
  categoryTree,
}: ProfilePageData) {
  function categoryName(sysNo) {
    for (const firstLevelCategory of categoryTree) {
      if (!firstLevelCategory.children?.length) {
        continue
      }

      for (const secondLevelCategory of firstLevelCategory.children) {
        if (secondLevelCategory.sysNo === sysNo) {
          return `${firstLevelCategory.name} / ${secondLevelCategory.name}`
        }
      }
    }
    return sysNo
  }

  return (
    <div className="profile-business-detail-wrap">
      <ProfileTitle title="企业信息" marginTop="0" marginBottom="18px" />
      <Row>
        <Col span={12}>
          <ProfileDetailItem label="年营业额" labelWidth="84px">
            {getEnumLabel({
              enums: enumState.SupplierIncomeAmountEnum,
              value: businessDetail.performance.incomeAmount,
            })}
          </ProfileDetailItem>
        </Col>
        <Col span={12}>
          <ProfileDetailItem label="供货品牌" labelWidth="84px">
            {businessDetail.brand?.brandList
              ?.map?.((item) => item.brandName)
              ?.join("，")}
          </ProfileDetailItem>
        </Col>
      </Row>

      <ProfileTitle title="历史合同" marginTop="30px" marginBottom="18px" />
      <Table
        pagination={false}
        rowKey={(row) => `${row.purchaseCategorySysNo}-${row.purchaserName}`}
        dataSource={businessDetail.performance.contractList}
        columns={[
          {
            title: "序号",
            dataIndex: "purchaseCategorySysNo",
            key: "purchaseCategorySysNo",
            render: (_value: any, _record: any, index: number) => (
              <span>{index + 1}</span>
            ),
          },
          {
            title: "采购品类",
            dataIndex: "purchaseCategoryName",
            key: "purchaseCategoryName",
            render: (val: string) => <span>{categoryName(val)}</span>,
          },
          {
            title: "采购方",
            dataIndex: "purchaserName",
            key: "purchaserName",
          },
          {
            title: "合同金额",
            dataIndex: "contractAmount",
            key: "contractAmount",
            render: (val: number) => <span>{val}万</span>,
          },
          {
            title: "签订日期",
            dataIndex: "contractDate",
            key: "contractDate",
            render: (val: number) => <span>{dateFormat(val)}</span>,
          },
        ]}
      />
      <ProfileTitle title="物流信息" marginTop="40px" marginBottom="18px" />
      <Table
        pagination={false}
        rowKey={(row) => `${row.areaSysNo}-${row.deliveryPeriod}`}
        dataSource={businessDetail.logistics.deliveryList}
        columns={[
          {
            title: "序号",
            dataIndex: "areaSysNo",
            key: "areaSysNo",
            render: (_value: any, _record: any, index: number) => (
              <span>{index + 1}</span>
            ),
          },
          {
            title: "配送地区",
            dataIndex: "areaName",
            key: "areaName",
          },
          {
            title: "配送时效",
            dataIndex: "deliveryPeriod",
            key: "deliveryPeriod",
            render: (val: number) => (
              <span>
                {getEnumLabel({
                  enums: enumState.SupplierDeliveryLimitationEnum,
                  value: val,
                })}
              </span>
            ),
          },
          {
            title: "配送方式",
            dataIndex: "deliveryWay",
            key: "deliveryWay",
            render: (val: number) => (
              <span>
                {getEnumLabel({
                  enums: enumState.SupplierDeliveryWayEnum,
                  value: val,
                })}
              </span>
            ),
          },
        ]}
      />
      <ProfileTitle title="员工配置" marginTop="40px" marginBottom="18px" />
      <Table
        pagination={false}
        rowKey={(row) =>
          `${row.idCard}-${row.phone}-${row.name}-${row.responsibilityScope}`
        }
        dataSource={businessDetail.staff.staffList}
        columns={[
          {
            title: "序号",
            dataIndex: "idCard",
            key: "idCard",
            render: (_value: any, _record: any, index: number) => (
              <span>{index + 1}</span>
            ),
          },
          {
            title: "姓名",
            dataIndex: "name",
            key: "name",
          },
          {
            title: "联系方式",
            dataIndex: "phone",
            key: "phone",
          },
          {
            title: "职责范围",
            dataIndex: "responsibilityScope",
            key: "responsibilityScope",
            render: (val: number) => (
              <span>
                {getEnumLabel({
                  enums: enumState.SupplierResponsibilityScopeEnum,
                  value: val,
                })}
              </span>
            ),
          },
        ]}
      />
      <ProfileTitle title="仓储能力" marginTop="40px" marginBottom="18px" />
      <Table
        pagination={false}
        rowKey={(row) =>
          `${row.areaSysNo}-${row.inventoryAmount}-${row.address}`
        }
        dataSource={businessDetail.warehouse.warehouseList}
        columns={[
          {
            title: "序号",
            dataIndex: "areaSysNo",
            key: "areaSysNo",
            render: (_value: any, _record: any, index: number) => (
              <span>{index + 1}</span>
            ),
          },
          {
            title: "仓库所在地",
            dataIndex: "areaName",
            key: "areaName",
          },
          {
            title: "仓储面积",
            dataIndex: "area",
            key: "area",
            render: (val: number) => (
              <span>
                {getEnumLabel({
                  enums: enumState.SupplierWarehouseAreaEnum,
                  value: val,
                })}
              </span>
            ),
          },
          {
            title: "库存金额",
            dataIndex: "inventoryAmount",
            key: "inventoryAmount",
            render: (val: number) => (
              <span>
                {getEnumLabel({
                  enums: enumState.SupplierWarehouseStockAmountEnum,
                  value: val,
                })}
              </span>
            ),
          },
          {
            title: "详细地址",
            dataIndex: "address",
            key: "address",
          },
        ]}
      />

      <style jsx>{`
        .profile-business-detail-wrap {
          padding: 24px 20px 22px 20px;
          line-height: 1;
        }
        .profile-business-detail-wrap
          :global(.ant-table-thead
            > tr
            > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before) {
          display: none;
        }

        .profile-business-detail-wrap
          :global(.ant-table-thead > tr > th, .ant-table-tbody
            > tr
            > td, .ant-table tfoot > tr > th, .ant-table tfoot > tr > td) {
          padding-top: 9px;
          padding-bottom: 9px;
        }

        .profile-business-detail-wrap :global(.ant-table table) {
          border: 1px solid #f0f0f0;
        }
      `}</style>
    </div>
  )
}
